/**
 * Event hooks for managing life events and timeline markers
 *
 * Provides hooks for fetching, creating, updating, and deleting events
 * using TanStack Query with proper caching, optimistic updates, and error handling.
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createApiClient,
  ApiClientError,
} from "@floline/api-client";
import type {
  EventResponse,
  CreateEventRequest,
  UpdateEventRequest,
  ListEventsResponse,
} from "@floline/api-client";
import { API_URL } from "@/env";

/** Query key factory for events */
export const eventKeys = {
  all: ["events"] as const,
  lists: () => [...eventKeys.all, "list"] as const,
  list: (filters: EventFilters) => [...eventKeys.lists(), filters] as const,
  infiniteLists: () => [...eventKeys.all, "infinite-list"] as const,
  infiniteList: (filters: EventFilters, pageSize: number) =>
    [...eventKeys.infiniteLists(), filters, pageSize] as const,
  details: () => [...eventKeys.all, "detail"] as const,
  detail: (id: string) => [...eventKeys.details(), id] as const,
};

/** API client configured for event requests */
const apiClient = createApiClient(
  API_URL
);

/** Filters for listing events */
export interface EventFilters {
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
  eventType?: string;
  relatedMedicationId?: string;
  q?: string;
}

interface InfiniteEventsOptions extends EventFilters {
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Fetch a list of events from the API with optional filters
 *
 * @param filters - Optional filters for pagination and date range
 * @returns List events response with data and metadata
 */
async function fetchEvents(filters?: EventFilters): Promise<ListEventsResponse> {
  return await apiClient.listEvents(filters);
}

/**
 * Fetch a single event by ID from the API
 *
 * @param id - Event ID
 * @returns Event data or null if not found
 */
async function fetchEvent(id: string): Promise<EventResponse | null> {
  try {
    const response = await apiClient.getEvent(id);
    return response.data;
  } catch (error: unknown) {
    // 404 means event does not exist
    if (error instanceof ApiClientError && error.status === 404) {
      return null;
    }
    // Re-throw other errors to be handled by React Query
    throw error;
  }
}

/**
 * Hook to fetch a list of events with optional filters
 *
 * Returns paginated event data along with loading, error states, and metadata.
 * Supports filtering by date range and event type.
 *
 * @param filters - Optional filters for pagination and filtering
 *
 * @example
 * ```tsx
 * function EventsListPage() {
 *   const { events, isLoading, total, hasMore } = useEvents({ 
 *     limit: 20, 
 *     startDate: '2026-01-01' 
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *   return (
 *     <div>
 *       <EventsList events={events} />
 *       {hasMore && <LoadMoreButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEvents(filters?: EventFilters) {
  const query = useQuery({
    queryKey: eventKeys.list(filters ?? {}),
    queryFn: () => fetchEvents(filters),
    // Events are relatively stable, can cache for a bit
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Retry on failure except for 401/404
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && (error.status === 401 || error.status === 404)) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const limit = filters?.limit ?? 10;
  const offset = filters?.offset ?? 0;
  const total = query.data?.meta.total ?? 0;
  const hasMore = offset + limit < total;

  return {
    /** Array of events */
    events: query.data?.data ?? [],
    /** Whether events are currently being fetched */
    isLoading: query.isLoading,
    /** Whether the fetch failed with an error */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Total number of events available */
    total,
    /** Whether there are more events to load */
    hasMore,
    /** Refetch the events manually */
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch events incrementally with infinite pagination
 *
 * Useful for timeline-style views where we want a small initial payload
 * and optional progressive loading (manual or automatic).
 */
export function useInfiniteEvents(options: InfiniteEventsOptions = {}) {
  const {
    pageSize = 50,
    enabled = true,
    ...filters
  } = options;

  const query = useInfiniteQuery({
    queryKey: eventKeys.infiniteList(filters, pageSize),
    queryFn: ({ pageParam = 0 }) =>
      fetchEvents({
        ...filters,
        limit: pageSize,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.meta.offset + lastPage.meta.limit;
      return nextOffset < lastPage.meta.total ? nextOffset : undefined;
    },
    initialPageParam: 0,
    enabled,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && (error.status === 401 || error.status === 404)) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const pages = query.data?.pages ?? [];
  const events = pages.flatMap((page) => page.data);
  const total = pages[0]?.meta.total ?? 0;

  return {
    events,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetching: query.isFetching,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch a single event by ID
 *
 * Returns the event data along with loading and error states.
 * Returns null if the event does not exist (404).
 * If id is null, the query is disabled.
 *
 * @param id - Event ID or null to disable the query
 *
 * @example
 * ```tsx
 * function EventDetailPage() {
 *   const { eventId } = useParams();
 *   const { event, isLoading, isError } = useEvent(eventId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (!event) return <EventNotFound />;
 *   return <EventDetail event={event} />;
 * }
 * ```
 */
export function useEvent(id: string | null) {
  const query = useQuery({
    queryKey: eventKeys.detail(id ?? ""),
    queryFn: () => {
      if (!id) {
        throw new Error("Event ID is required");
      }
      return fetchEvent(id);
    },
    // Only run query if we have an ID
    enabled: id !== null,
    // Events are relatively stable, can cache for a bit
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Don't retry on 404s (expected when event doesn't exist)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && (error.status === 404 || error.status === 401)) {
        return false;
      }
      return failureCount < 3;
    },
    // Don't refetch on mount if data is fresh
    refetchOnMount: false,
  });

  return {
    /** Event data, or null if not found */
    event: query.data ?? null,
    /** Whether the event is currently being fetched */
    isLoading: query.isLoading,
    /** Whether the fetch failed with an error (excluding 404) */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Refetch the event manually */
    refetch: query.refetch,
  };
}

/**
 * Hook to create a new event
 *
 * Provides a mutation function for creating events.
 * Automatically invalidates event lists on success.
 *
 * @example
 * ```tsx
 * function CreateEventForm() {
 *   const { createEvent, isCreating, error } = useCreateEvent();
 *
 *   const handleSubmit = async (data: CreateEventRequest) => {
 *     try {
 *       const event = await createEvent(data);
 *       navigate(`/events/${event.id}`);
 *     } catch (err) {
 *       // Error is available in error state
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {error && <ErrorMessage error={error} />}
 *       <button disabled={isCreating}>Create Event</button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateEventRequest) => {
      const response = await apiClient.createEvent(data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all event lists to refetch with new event
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
    },
  });

  return {
    /** Create a new event */
    createEvent: mutation.mutateAsync,
    /** Whether a creation is in progress */
    isCreating: mutation.isPending,
    /** Error from the last creation attempt */
    error: mutation.error,
  };
}

/**
 * Hook to update an existing event
 *
 * Provides a mutation function for updating events with optimistic updates.
 * Automatically invalidates event lists and detail on success.
 *
 * @example
 * ```tsx
 * function EditEventForm({ eventId }: { eventId: string }) {
 *   const { updateEvent, isUpdating, error } = useUpdateEvent();
 *
 *   const handleSubmit = async (data: UpdateEventRequest) => {
 *     try {
 *       await updateEvent({ id: eventId, data });
 *       toast.success('Event updated');
 *     } catch (err) {
 *       // Error is available in error state
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {error && <ErrorMessage error={error} />}
 *       <button disabled={isUpdating}>Save Changes</button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateEventRequest }) => {
      const response = await apiClient.updateEvent(id, data);
      return response.data;
    },
    // Optimistic update
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: eventKeys.detail(id) });

      // Snapshot the previous value
      const previousEvent = queryClient.getQueryData<EventResponse | null>(
        eventKeys.detail(id)
      );

      // Optimistically update the cache
      if (previousEvent) {
        queryClient.setQueryData<EventResponse>(
          eventKeys.detail(id),
          {
            ...previousEvent,
            ...(data.eventType && { eventType: data.eventType }),
            ...(data.title && { title: data.title }),
            ...(data.startDate && { startDate: data.startDate }),
            ...(data.endDate !== undefined && { endDate: data.endDate }),
            ...(data.notes !== undefined && { notes: data.notes }),
            ...(data.locationId !== undefined && { locationId: data.locationId }),
            ...(data.relatedMedicationId !== undefined && { relatedMedicationId: data.relatedMedicationId }),
            ...(data.confidenceLevel && { confidenceLevel: data.confidenceLevel }),
            ...(data.attachments !== undefined && { attachments: data.attachments }),
            updatedAt: new Date().toISOString(),
          }
        );
      }

      return { previousEvent };
    },
    // On error, rollback to the previous value
    onError: (_err, { id }, context) => {
      if (context?.previousEvent) {
        queryClient.setQueryData(
          eventKeys.detail(id),
          context.previousEvent
        );
      }
    },
    // Always refetch after error or success
    onSettled: (_data, _error, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
    },
  });

  return {
    /** Update an event */
    updateEvent: mutation.mutateAsync,
    /** Whether an update is in progress */
    isUpdating: mutation.isPending,
    /** Error from the last update attempt */
    error: mutation.error,
  };
}

/**
 * Hook to delete an event
 *
 * Provides a mutation function for deleting events with optimistic updates.
 * Automatically invalidates event lists on success.
 *
 * @example
 * ```tsx
 * function DeleteEventButton({ eventId }: { eventId: string }) {
 *   const { deleteEvent, isDeleting, error } = useDeleteEvent();
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure?')) return;
 *     
 *     try {
 *       await deleteEvent(eventId);
 *       navigate('/events');
 *     } catch (err) {
 *       // Error is available in error state
 *     }
 *   };
 *
 *   return (
 *     <>
 *       {error && <ErrorMessage error={error} />}
 *       <button onClick={handleDelete} disabled={isDeleting}>
 *         {isDeleting ? 'Deleting...' : 'Delete Event'}
 *       </button>
 *     </>
 *   );
 * }
 * ```
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteEvent(id);
      return id;
    },
    // Optimistic update: remove from lists
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: eventKeys.lists() });

      // Snapshot the previous lists
      const previousLists = queryClient.getQueriesData<ListEventsResponse>({
        queryKey: eventKeys.lists(),
      });

      // Optimistically remove from all lists
      queryClient.setQueriesData<ListEventsResponse>(
        { queryKey: eventKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((event) => event.id !== id),
            meta: {
              ...old.meta,
              total: Math.max(0, old.meta.total - 1),
            },
          };
        }
      );

      return { previousLists };
    },
    // On error, rollback to the previous values
    onError: (_err, _id, context) => {
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    // Always refetch after error or success
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.removeQueries({ queryKey: eventKeys.detail(id) });
    },
  });

  return {
    /** Delete an event */
    deleteEvent: mutation.mutateAsync,
    /** Whether a deletion is in progress */
    isDeleting: mutation.isPending,
    /** Error from the last deletion attempt */
    error: mutation.error,
  };
}
